import { useState, useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Upload, Building, Image, Settings, Brain, Info, Phone, Link as LinkIcon, Palette, Wand2, Globe, CheckCircle2, ChevronDown, ChevronUp, Sparkles, Database } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Json } from "@/types/database.types";
import { useAutoSave } from "@/hooks/useAutoSave";
import { FirecrawlService } from "@/utils/FirecrawlService";
import { Switch } from "@/components/ui/switch";
import { industryService } from "@/services/industryService";
import { brandService } from "@/services/brandService";
import AddKnowledgeItemDialog from "@/components/knowledge/AddKnowledgeItemDialog";
import { useKnowledgeItems } from "@/hooks/knowledge/useKnowledgeItems";
import SettingsContentWrapper, { SettingsBlock } from "./SettingsContentWrapper";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const generalSetupSchema = z.object({
  name: z.string().min(2, { message: "Organization name must be at least 2 characters" }),
  industry_id: z.string().optional(),
  // hubflo_api_key: z.string().optional(),
  brand_color: z.string().regex(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/, {
    message: "Please enter a valid hex color code (e.g., #3B82F6)",
  }).default("#3B82F6").optional(),
  business_overview: z.string().optional(),
  services_overview: z.string().optional(),
  legal_disclaimer: z.string().optional(),
  contact_email: z.string().email("Invalid email").optional().or(z.literal("")),
  contact_phone: z.string().optional(),
  contact_website: z.string().url("Invalid URL").optional().or(z.literal("")),
  tone_of_voice: z.string().optional(),
  response_length: z.string().optional(),
  general_instructions: z.string().optional(),
  general_restrictions: z.string().optional(),
  website_content: z.string().optional(),
});

type FormValues = z.infer<typeof generalSetupSchema>;

const toneOptions = [
  { value: "Professional", label: "Professional", description: "Use formal language, avoid casual expressions, maintain a business-appropriate tone at all times. Be respectful, precise, and thorough while avoiding humor or personal anecdotes." },
  { value: "Friendly", label: "Friendly", description: "Adopt a warm, approachable tone that balances professionalism with conversational friendliness. Use occasional light humor where appropriate and show empathy in responses. Aim to sound like a helpful colleague." },
  { value: "Casual", label: "Casual", description: "Use relaxed, everyday language that feels conversational and informal. Feel free to use common expressions, contractions, and a more personal tone. Be friendly and relatable while remaining respectful." },
  { value: "Technical", label: "Technical", description: "Employ precise terminology and detailed explanations. Prioritize accuracy and thoroughness over simplicity. Use industry-standard terms and concepts, and provide context for technical information." },
  { value: "Empathetic", label: "Empathetic", description: "Focus on understanding and acknowledging user feelings. Use supportive language, validate concerns, and demonstrate active listening. Show compassion and patience in all interactions." },
];

const responseOptions = [
  { value: "Balanced", label: "Balanced", description: "Provide comprehensive answers that cover all necessary details while remaining concise. Aim for 2-3 paragraphs for complex questions, with bulleted lists where appropriate. Strike a balance between thoroughness and brevity." },
  { value: "Concise", label: "Concise", description: "Keep responses brief and to the point. Use short paragraphs, focus only on the most essential information, and minimize examples unless specifically requested. Ideal for busy users who need quick answers." },
  { value: "Detailed", label: "Detailed", description: "Provide thorough, comprehensive responses that explore all aspects of a question. Include examples, context, and additional relevant information. Use multiple paragraphs, lists, and structured information where helpful." },
  { value: "Minimal", label: "Minimal", description: "Provide the shortest possible answers that still address the question. Use single sentences or very short paragraphs. Focus exclusively on answering exactly what was asked with no additional information." },
  { value: "Comprehensive", label: "Comprehensive", description: "Deliver exhaustive responses that cover every angle of a topic. Include detailed explanations, multiple examples, potential alternatives, and related considerations. Prioritize completeness over brevity." },
];

const GeneralSettingsContent = () => {
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [industries, setIndustries] = useState<any[]>([]);
  const [isLoadingIndustries, setIsLoadingIndustries] = useState(true);
  const { organization, refreshProfile } = useAuth();
  const { toast } = useToast();

  const [showWebsiteContent, setShowWebsiteContent] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState<FormValues | null>(null);
  const [showAddLinkDialog, setShowAddLinkDialog] = useState(false);
  const [isFetchingBrand, setIsFetchingBrand] = useState(false);
  const [showUpdateAgentUrlsModal, setShowUpdateAgentUrlsModal] =
    useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGenerationComplete, setIsGenerationComplete] = useState(false);
  const [isCrawling, setIsCrawling] = useState(false);

  // Fetch organization-level link knowledge items to check if website URL already exists
  const { items: linkItems } = useKnowledgeItems("link", null);

  const form = useForm<FormValues>({
    resolver: zodResolver(generalSetupSchema),
    defaultValues: {
      name: "",
      industry_id: "",
      // hubflo_api_key: "",
      brand_color: "#3B82F6",
      business_overview: "",
      services_overview: "",
      legal_disclaimer: "",
      contact_email: "",
      contact_phone: "",
      contact_website: "",
      tone_of_voice: "Professional",
      response_length: "Balanced",
      general_instructions: "",
      general_restrictions: "",
      website_content: "",
    },
  });

  // Check if organization website URL already exists as a knowledge item
  const websiteUrl = form.watch('contact_website');
  const hasExistingWebsiteItem = linkItems.some(item => 
    item.url && websiteUrl && item.url.toLowerCase() === websiteUrl.toLowerCase()
  );

  const { isSaving, setDirty } = useAutoSave({
    onSave: async () => {
      const values = form.getValues();
      return await onSubmit(values);
    },
    successMessage: "Settings saved automatically",
    errorMessage: "Failed to auto-save settings",
  });

  // Set up form change subscriptions for auto-save
  useEffect(() => {
    const subscription = form.watch((value, { name, type }) => {
      if (!initialFormValues) return; // Don't trigger auto-save until initial values are set
      
      // Get the current form values
      const currentValues = form.getValues();
      
      // Check if any value has actually changed from initial values
      const hasChanges = Object.keys(currentValues).some(key => {
        return currentValues[key as keyof FormValues] !== initialFormValues[key as keyof FormValues];
      });

      // Only trigger auto-save if there are actual changes
      if (hasChanges) {
        setInitialFormValues(currentValues);
        setDirty();
      }
    });
    
    return () => subscription.unsubscribe();
  }, [form, setDirty, initialFormValues]);

  // Helper function to reset form with organization data
  const resetFormWithOrganizationData = (orgData: any) => {
    const contactInfo = orgData.contact_info as { email?: string; phone?: string; website?: string } | null;
    
    const formValues = {
      name: orgData.name || "",
      industry_id: orgData.industry_id || "",
      brand_color: orgData.brand_color || "#3B82F6",
      business_overview: orgData.business_overview || "",
      services_overview: orgData.services_overview || "",
      legal_disclaimer: orgData.legal_disclaimer || "",
      contact_email: contactInfo?.email || "",
      contact_phone: contactInfo?.phone || "",
      contact_website: orgData.website || "",
      tone_of_voice: orgData.tone_of_voice || "Professional",
      response_length: orgData.response_length || "Balanced",
      general_instructions: orgData.general_instructions || "",
      general_restrictions: orgData.general_restrictions || "",
      website_content: orgData.website_content || "",
    };

    // Set initial form values and reset form with new values
    setInitialFormValues(formValues);
    form.reset(formValues);
    
    return formValues;
  };

  // Load form data when organization changes
  useEffect(() => {
    if (organization) {
      resetFormWithOrganizationData(organization);
      setIsLoading(false);
    } else {
      console.log("Waiting for organization data...");
    }
  }, [organization, form]);

  // Load industries on component mount
  useEffect(() => {
    const loadIndustries = async () => {
      setIsLoadingIndustries(true);
      const { data, error } = await industryService.getAllIndustries();
      if (error) {
        console.error("Failed to load industries:", error);
        toast({
          variant: "destructive",
          title: "Failed to load industries",
          description: error.message,
        });
      } else {
        setIndustries(data || []);
      }
      setIsLoadingIndustries(false);
    };

    loadIndustries();
  }, []);

  const onSubmit = async (data: FormValues, updateAgentUrls?: boolean) => {
    if (!organization?.id) return;

    setIsSubmitting(true);
    try {
      // Handle file uploads first with validation
      let logoUrl = organization.logo_url;
      let bannerUrl = organization.banner_url;

      if (logoFile) {
        // Validate logo file
        const maxSize = 6; // 6MB
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
        
        if (!allowedTypes.includes(logoFile.type)) {
          throw new Error('Invalid logo file type. Please upload JPG, PNG, GIF, SVG, or WebP images.');
        }
        
        if (logoFile.size > maxSize * 1024 * 1024) {
          throw new Error(`Logo file size exceeds ${maxSize}MB limit.`);
        }
        
        const logoFileExt = logoFile.name.split('.').pop()?.toLowerCase();
        const validExtensions = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'];
        const safeLogoExtension = logoFileExt && validExtensions.includes(logoFileExt) ? logoFileExt : 'jpg';
        const logoFileName = `logo-${organization.id}.${safeLogoExtension}`;
        
        const { error: logoUploadError } = await supabase.storage
          .from('organization-assets')
          .upload(logoFileName, logoFile, { 
            upsert: true,
            contentType: logoFile.type,
          });

        if (logoUploadError) throw logoUploadError;

        const { data: logoPublicUrl } = supabase.storage
          .from('organization-assets')
          .getPublicUrl(logoFileName);

        logoUrl = logoPublicUrl.publicUrl;
      }

      if (bannerFile) {
        // Validate banner file
        const maxSize = 6; // 6MB
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
        
        if (!allowedTypes.includes(bannerFile.type)) {
          throw new Error('Invalid banner file type. Please upload JPG, PNG, GIF, SVG, or WebP images.');
        }
        
        if (bannerFile.size > maxSize * 1024 * 1024) {
          throw new Error(`Banner file size exceeds ${maxSize}MB limit.`);
        }
        
        const bannerFileExt = bannerFile.name.split('.').pop()?.toLowerCase();
        const validExtensions = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'];
        const safeBannerExtension = bannerFileExt && validExtensions.includes(bannerFileExt) ? bannerFileExt : 'jpg';
        const bannerFileName = `banner-${organization.id}.${safeBannerExtension}`;
        
        const { error: bannerUploadError } = await supabase.storage
          .from('organization-assets')
          .upload(bannerFileName, bannerFile, { 
            upsert: true,
            contentType: bannerFile.type,
          });

        if (bannerUploadError) throw bannerUploadError;

        const { data: bannerPublicUrl } = supabase.storage
          .from('organization-assets')
          .getPublicUrl(bannerFileName);

        bannerUrl = bannerPublicUrl.publicUrl;
      }

      // Prepare contact_info object
      const contactInfo = {
        email: data.contact_email || null,
        phone: data.contact_phone || null,
      };

      // Update organization

      let updatePayload: any;
      if (updateAgentUrls) {
        updatePayload = { agent_url: data.name };
        const myChannel = supabase.channel("org-tracking");
        myChannel
          .send({
            type: "broadcast",
            event: "agent_url_changed",
            payload: { agent_url: data.name },
          })
          .then((resp) => console.log(resp));
      } else {
        updatePayload = {
          name: data.name,
          industry_id: data.industry_id || null,
          brand_color: data.brand_color || null,
          business_overview: data.business_overview || null,
          services_overview: data.services_overview || null,
          legal_disclaimer: data.legal_disclaimer || null,
          contact_info: contactInfo,
          website: data.contact_website || null, // Save website at organization level
          tone_of_voice: data.tone_of_voice || null,
          response_length: data.response_length || null,
          general_instructions: data.general_instructions || null,
          general_restrictions: data.general_restrictions || null,
          website_content: data.website_content || null,
          logo_url: logoUrl,
          banner_url: bannerUrl,
        };
      }

      const { error: updateError } = await supabase
        .from("organizations")
        .update(updatePayload)
        .eq("id", organization.id);

      if (updateError) throw updateError;

      await refreshProfile();
      toast({
        title: "Settings updated",
        description: "Your organization settings have been saved successfully.",
      });

      // Reset file states
      setLogoFile(null);
      setBannerFile(null);
    } catch (error: any) {
      console.error("Failed to update organization:", error);
      toast({
        variant: "destructive",
        title: "Failed to update settings",
        description: error.message || "An unexpected error occurred.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setLogoFile(e.target.files[0]);
    }
  };

  const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setBannerFile(e.target.files[0]);
    }
  };

  // Brand Fetch function to get brand data
  const fetchBrandData = async () => {
    const websiteUrl = form.getValues("contact_website");
    
    if (!websiteUrl) {
      toast({
        variant: "destructive",
        title: "Website URL Required",
        description: "Please enter a valid website URL to fetch brand data.",
      });
      return;
    }

    if (!organization?.id) {
      toast({
        variant: "destructive",
        title: "Organization Required",
        description: "No organization found. Please ensure you're logged in.",
      });
      return;
    }

    setIsFetchingBrand(true);

    try {
      console.log("Calling Brand Fetch with:", {
        organizationId: organization.id,
        websiteUrl: websiteUrl
      });

      const result = await brandService.fetchBrandData(organization.id);

      if (result.success) {
        toast({
          title: "Brand data updated successfully!",
          description: `Information automatically populated from your website.`,
        });

        // Refresh the profile to show updated data
        await refreshProfile();
        
        // Refresh form with updated organization data - get the latest data
        const { data: updatedOrg, error: orgError } = await supabase
          .from('organizations')
          .select('*')
          .eq('id', organization.id)
          .single();

        if (!orgError && updatedOrg) {
          resetFormWithOrganizationData(updatedOrg);
        }
      } else {
        throw new Error(result.error || "No data returned from Brand Fetch");
      }
    } catch (error: any) {
      console.error("Error fetching brand data:", error);
      toast({
        variant: "destructive",
        title: "🙈 Oh no! We were not able to fetch information from your website!",
        description: "Try again or enter your organization information manually.",
      });
    } finally {
      setIsFetchingBrand(false);
    }
  };

  // Handle AI generation
  const handleGenerateWithAI = async () => {
    const websiteContent = form.getValues('website_content');
    const websiteUrl = form.getValues('contact_website');
    
    if (!websiteContent && !websiteUrl) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please add website content or URL to generate AI content.",
      });
      return;
    }

    setIsGenerating(true);
    try {
      // This is a placeholder for AI generation functionality
      // In the actual implementation, this would call an AI service
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate API call
      
      setIsGenerationComplete(true);
      toast({
        title: "Content generated successfully!",
        description: "Business information has been populated using AI.",
      });
    } catch (error) {
      console.error("Error generating content:", error);
      toast({
        variant: "destructive",
        title: "Generation failed",
        description: "Unable to generate content. Please try again.",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle extract content
  const handleExtractContent = async () => {
    const websiteUrl = form.getValues('contact_website');
    
    if (!websiteUrl) {
      toast({
        variant: "destructive",
        title: "Website URL Required",
        description: "Please enter a valid website URL to extract content.",
      });
      return;
    }

    setIsCrawling(true);
    try {
      const firecrawlService = new FirecrawlService();
      
      // Crawl the website
      const result = await firecrawlService.crawlWebsite(websiteUrl);
      
      if (result && result.success) {
        const content = (result.data as any)?.content;
        if (content) {
          // Clean up and limit the content
          const cleanedContent = content.replace(/\s+/g, ' ').trim();
          const limitedContent = cleanedContent.length > 10000 
            ? cleanedContent.substring(0, 10000) + "..."
            : cleanedContent;
          
          form.setValue('website_content', limitedContent);
          
          toast({
            title: "Website content extracted!",
            description: "Content has been added to your knowledge base.",
          });
        }
      } else {
        throw new Error("Failed to extract website content");
      }
    } catch (error) {
      console.error("Error extracting content:", error);
      toast({
        variant: "destructive",
        title: "Extraction failed",
        description: "Unable to extract website content. Please try again.",
      });
    } finally {
      setIsCrawling(false);
    }
  };

  // Handle add link success
  const handleAddLinkSuccess = () => {
    toast({
      title: "Link added successfully!",
      description: "The website has been added to your knowledge base.",
    });
    setShowAddLinkDialog(false);
  };

  if (isLoading) {
    return (
      <SettingsContentWrapper
        title="General Settings"
        description="Configure your organization's general settings, business information, and AI behavior"
      >
        <div className="container mx-auto py-8 space-y-6 max-w-4xl">
          <Skeleton className="h-6 w-3/4 mb-6" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-60 w-full" />
        </div>
      </SettingsContentWrapper>
    );
  }

  return (
    <SettingsContentWrapper
      title="General Settings"
      description="Configure your organization's general settings, business information, and AI behavior"
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          
          {/* Organization Details */}
          <SettingsBlock 
            icon={<Building className="h-5 w-5" />}
            title="Organization Details"
            iconBgColor="bg-blue-100"
            iconTextColor="text-blue-500"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-gray-700">
                    Organization Name *
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Acme Inc."
                      {...field}
                      disabled={isSubmitting || isSaving}
                    />
                  </FormControl>
                  <FormMessage />
                  {organization?.name !== organization?.agent_url && (
                    <div className="text-xs text-muted-foreground mt-1">
                      Just updated your organization name? You can update
                      all your agent URLs{" "}
                      <button
                        type="button"
                        className="underline text-blue-600 hover:text-blue-800"
                        onClick={() => setShowUpdateAgentUrlsModal(true)}
                      >
                        here
                      </button>
                    </div>
                  )}
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="industry_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-gray-700">
                    Industry
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={isSubmitting || isSaving || isLoadingIndustries}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={isLoadingIndustries ? "Loading industries..." : "Select your industry"} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {industries.map((industry) => (
                        <SelectItem key={industry.id} value={industry.id}>
                          {industryService.getDisplayName(industry)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </SettingsBlock>

          {/* Website Integration */}
          <SettingsBlock 
            icon={<Globe className="h-5 w-5" />}
            title="Website Integration"
            iconBgColor="bg-green-100"
            iconTextColor="text-green-500"
          >
            <FormField
              control={form.control}
              name="contact_website"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-gray-700">Website URL</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="https://example.com"
                      {...field}
                      value={field.value || ""}
                      disabled={isSaving}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Show buttons only if website URL exists */}
            {websiteUrl && (
              <div className="space-y-3">
                <div className="flex gap-3">
                  <Button
                    type="button"
                    onClick={fetchBrandData}
                    disabled={isFetchingBrand || isSubmitting || isSaving}
                    className="flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-700"
                  >
                    {isFetchingBrand ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <Wand2 className="h-4 w-4" />
                    )}
                    {isFetchingBrand ? "Fetching..." : "Get Company Branding & Data from website"}
                  </Button>
                </div>
              </div>
            )}
            
            {/* Show info when URL already exists in knowledge base */}
            {websiteUrl && hasExistingWebsiteItem && (
              <div className="flex justify-center">
                <div className="w-full p-3 bg-green-50 border border-green-200 rounded-md">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="text-sm">Website content already added to knowledge base</span>
                  </div>
                </div>
              </div>
            )}
          </SettingsBlock>

          {/* Contact Information */}
        <SettingsBlock 
          icon={<Phone className="h-5 w-5" />}
          title="Contact Information"
          iconBgColor="bg-purple-100"
          iconTextColor="text-purple-500"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="contact_email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-gray-700">Contact Email</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="contact@yourcompany.com"
                      {...field}
                      value={field.value || ""}
                      disabled={isSubmitting || isSaving}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="contact_phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-gray-700">Contact Phone</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="+1 (555) 123-4567"
                      {...field}
                      value={field.value || ""}
                      disabled={isGenerating || isSubmitting || isSaving}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </SettingsBlock>

        {/* Business Information */}
        <SettingsBlock 
          icon={<Info className="h-5 w-5" />}
          title="Business Information"
          iconBgColor="bg-amber-100"
          iconTextColor="text-amber-500"
        >
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="business_overview"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-gray-700">Business Overview</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe what your organization does, your mission, and key activities..."
                      className="min-h-24"
                      {...field}
                      value={field.value || ""}
                      disabled={isGenerating || isSubmitting || isSaving}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="services_overview"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-gray-700">Services Overview</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="List and describe the main services or products you offer..."
                      className="min-h-24"
                      {...field}
                      value={field.value || ""}
                      disabled={isGenerating || isSubmitting || isSaving}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </SettingsBlock>

        {/* Legal Information */}
        <SettingsBlock 
          icon={<Settings className="h-5 w-5" />}
          title="Legal Information"
          iconBgColor="bg-gray-100"
          iconTextColor="text-gray-500"
        >
          <p className="text-sm text-gray-600 mb-4">
            Legal disclaimers and important legal information
          </p>
          <FormField
            control={form.control}
            name="legal_disclaimer"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-gray-700">Legal Disclaimer</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Any legal disclaimers, terms of use, or important legal information..."
                    className="min-h-20"
                    {...field}
                    value={field.value || ""}
                    disabled={isGenerating || isSubmitting || isSaving}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </SettingsBlock>
        </form>
      </Form>

      {/* Add dialog */}
      <AddKnowledgeItemDialog
        open={showAddLinkDialog}
        onOpenChange={setShowAddLinkDialog}
        defaultUrl={form.getValues('contact_website')}
        defaultType="link"
        onSuccess={handleAddLinkSuccess}
        organizationLevel={true}
      />
      

      <Dialog
          open={showUpdateAgentUrlsModal}
          onOpenChange={setShowUpdateAgentUrlsModal}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update All Agent URLs & Widget Codes?</DialogTitle>
              <DialogDescription>
                <span className="text-red-600 font-semibold">Important:</span> If
                you shared your AI agents via link, you don't need to do anything,
                their URL will be updated automatically. If you embedded your
                agents via an embed code, you'll need to grab the new embed code
                and update it on your website.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="secondary"
                onClick={() => setShowUpdateAgentUrlsModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  onSubmit(form.getValues(), true);
                  setShowUpdateAgentUrlsModal(false);
                }}
              >
                Confirm
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </SettingsContentWrapper>
  );
};

export default GeneralSettingsContent; 